import { useState } from "react";
import { Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import API_BASE_URL from "../config";
import "./Admin.css";

const Admin = () => {
  const API_BASE = `${API_BASE_URL}/admin`;

  // Состояние аутентификации
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [csrfToken, setCsrfToken] = useState("");

  // Форма входа
  const [adminname, setAdminname] = useState("");
  const [adminpass, setAdminpass] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Форма управления доступом
  const [username, setUsername] = useState("");
  const [accessPoint, setAccessPoint] = useState("");
  const [currentSubstat, setCurrentSubstat] = useState(null);
  const [userExists, setUserExists] = useState(null);
  const [grantError, setGrantError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Модальное окно подтверждения
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingChange, setPendingChange] = useState(null);

  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!adminname || !adminpass) {
      setLoginError("Заполните все поля");
      return;
    }

    try {
      setIsLoggingIn(true);
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adminname, adminpass }),
      });

      const data = await res.json();

      if (!res.ok) {
        setLoginError(data.detail || "Ошибка входа");
        return;
      }

      // Сохраняем CSRF токен
      setCsrfToken(data.csrf_token);
      setIsAuthenticated(true);
      setAdminname("");
      setAdminpass("");
    } catch (err) {
      setLoginError("Ошибка соединения с сервером");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleCheckUser = async () => {
    if (!username) {
      setGrantError("Введите email пользователя");
      return;
    }

    if (!validateEmail(username)) {
      setGrantError("Неверный формат email");
      return;
    }

    try {
      setIsLoading(true);
      setGrantError("");
      const res = await fetch(
        `${API_BASE}/get-user-info?email=${encodeURIComponent(username)}`,
        {
          headers: { "X-CSRF-Token": csrfToken },
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setGrantError(data.detail || "Ошибка проверки пользователя");
        setUserExists(null);
        setCurrentSubstat(null);
        return;
      }

      setUserExists(data.exists);
      setCurrentSubstat(data.current_substat);

      if (!data.exists) {
        setGrantError("Пользователь с таким email не найден");
      }
    } catch (err) {
      setGrantError("Ошибка соединения с сервером");
      setUserExists(null);
      setCurrentSubstat(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrantAccessClick = () => {
    setGrantError("");

    if (!username) {
      setGrantError("Введите email пользователя");
      return;
    }

    if (!validateEmail(username)) {
      setGrantError("Неверный формат email");
      return;
    }

    if (accessPoint === "" || accessPoint < 0 || accessPoint > 9) {
      setGrantError("Access point должен быть от 0 до 9");
      return;
    }

    if (!userExists) {
      setGrantError("Сначала проверьте существование пользователя");
      return;
    }

    // Открываем модальное окно подтверждения
    setPendingChange({
      username,
      accessPoint: parseInt(accessPoint, 10),
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmGrant = async () => {
    if (!pendingChange) return;

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE}/grant-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: pendingChange.username,
          access_point: pendingChange.accessPoint,
          csrf_token: csrfToken,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setGrantError(data.detail || "Ошибка изменения доступа");
        setShowConfirmDialog(false);
        return;
      }

      // Успешно изменено
      setCurrentSubstat(pendingChange.accessPoint);
      setShowConfirmDialog(false);
      setPendingChange(null);
      setGrantError("");
      
      // Опционально: показать уведомление об успехе
      alert(`Доступ успешно изменен для ${pendingChange.username}`);
    } catch (err) {
      setGrantError("Ошибка соединения с сервером");
      setShowConfirmDialog(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    setPendingChange(null);
  };

  if (!isAuthenticated) {
    // Форма входа
    return (
      <div className="admin-page">
        <header className="admin-header">
          <div className="admin-header-brand">
            <Shield className="admin-header-icon" />
            <span className="admin-header-title">Admin Panel</span>
          </div>
        </header>

        <div className="admin-main">
          <div className="admin-card-wrapper">
            <div className="admin-card">
              <div className="admin-card-header">
                <h1 className="admin-card-title">Admin Login</h1>
                <p className="admin-card-subtitle">
                  Restricted access - authorized personnel only
                </p>
              </div>

              <form onSubmit={handleLogin} className="admin-form">
                <div className="admin-form-field">
                  <Label htmlFor="adminname" className="admin-label">
                    Admin Name
                  </Label>
                  <Input
                    id="adminname"
                    type="text"
                    placeholder="Enter admin name"
                    value={adminname}
                    onChange={(e) => {
                      setAdminname(e.target.value);
                      setLoginError("");
                    }}
                    className="admin-input"
                    required
                  />
                </div>

                <div className="admin-form-field">
                  <Label htmlFor="adminpass" className="admin-label">
                    Password
                  </Label>
                  <Input
                    id="adminpass"
                    type="password"
                    placeholder="Enter password"
                    value={adminpass}
                    onChange={(e) => {
                      setAdminpass(e.target.value);
                      setLoginError("");
                    }}
                    className="admin-input"
                    required
                  />
                </div>

                {loginError && <p className="admin-error">{loginError}</p>}

                <Button
                  type="submit"
                  className="admin-button"
                  disabled={isLoggingIn}
                >
                  {isLoggingIn ? "Logging in..." : "Login"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Панель управления доступом (после входа)
  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="admin-header-brand">
          <Shield className="admin-header-icon" />
          <span className="admin-header-title">Admin Panel - Access Control</span>
        </div>
      </header>

      <div className="admin-main">
        <div className="admin-card-wrapper">
          <div className="admin-card">
            <div className="admin-card-header">
              <h1 className="admin-card-title">Grant User Access</h1>
              <p className="admin-card-subtitle">
                Modify user subscription status (substat)
              </p>
            </div>

            <div className="admin-form">
              <div className="admin-form-field">
                <Label htmlFor="username" className="admin-label">
                  User Email
                </Label>
                <Input
                  id="username"
                  type="email"
                  placeholder="user@example.com"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setGrantError("");
                    setUserExists(null);
                    setCurrentSubstat(null);
                  }}
                  className="admin-input"
                />
              </div>

              <Button
                type="button"
                onClick={handleCheckUser}
                className="admin-button-secondary"
                disabled={isLoading}
              >
                {isLoading ? "Checking..." : "Check User"}
              </Button>

              {userExists !== null && (
                <div className={`admin-user-info ${userExists ? "exists" : "not-exists"}`}>
                  {userExists ? (
                    <>
                      <p className="admin-info-text">
                        ✓ User found
                      </p>
                      <p className="admin-info-text">
                        Current substat: <strong>{currentSubstat}</strong>
                      </p>
                    </>
                  ) : (
                    <p className="admin-info-text">✗ User not found</p>
                  )}
                </div>
              )}

              <div className="admin-form-field">
                <Label htmlFor="access-point" className="admin-label">
                  Access Point (0-9)
                </Label>
                <Input
                  id="access-point"
                  type="number"
                  min="0"
                  max="9"
                  placeholder="Enter value from 0 to 9"
                  value={accessPoint}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "" || (parseInt(val, 10) >= 0 && parseInt(val, 10) <= 9)) {
                      setAccessPoint(val);
                      setGrantError("");
                    }
                  }}
                  className="admin-input"
                />
              </div>

              {grantError && <p className="admin-error">{grantError}</p>}

              <Button
                type="button"
                onClick={handleGrantAccessClick}
                className="admin-button"
                disabled={isLoading || !userExists}
              >
                Grant Access
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно подтверждения */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="admin-confirm-modal">
          <DialogHeader>
            <DialogTitle>Confirm Access Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to change the access level for this user?
            </DialogDescription>
          </DialogHeader>
          
          {pendingChange && (
            <div className="admin-confirm-details">
              <p>
                <strong>User:</strong> {pendingChange.username}
              </p>
              <p>
                <strong>Current substat:</strong> {currentSubstat}
              </p>
              <p>
                <strong>New substat:</strong> {pendingChange.accessPoint}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={handleCancelConfirm}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmGrant}
              disabled={isLoading}
              className="admin-button-confirm"
            >
              {isLoading ? "Processing..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;

